import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-ancestral-green text-ancestral-cream py-8 border-t border-ancestral-gold/20" data-id="element-50">
        <div className="container mx-auto px-4" data-id="element-51">
          <div className="max-w-4xl mx-auto" data-id="element-52">
            {/* Main content */}
            <div className="text-center space-y-4" data-id="element-53">
              <p className="text-sm text-ancestral-cream/70" data-id="element-54">
                © {new Date().getFullYear()} El Gran Recetario Ancestral. Todos
                los derechos reservados.
              </p>

              {/* Links */}
              <div className="flex items-center justify-center gap-6 text-sm" data-id="element-55">
                <button onClick={() => setOpenModal('privacy')} className="text-ancestral-gold hover:text-ancestral-cream transition-colors underline underline-offset-2" data-id="element-56">
                  Política de Privacidad
                </button>
                <span className="text-ancestral-cream/30" data-id="element-57">|</span>
                <button onClick={() => setOpenModal('terms')} className="text-ancestral-gold hover:text-ancestral-cream transition-colors underline underline-offset-2" data-id="element-58">
                  Términos y Condiciones
                </button>
              </div>

              <p className="text-xs text-ancestral-cream/50 max-w-2xl mx-auto leading-relaxed" data-id="element-59">
                Este producto es informativo y educativo. No reemplaza el
                consejo médico profesional. Consulta a tu médico antes de usar
                cualquier remedio natural, especialmente si estás tomando
                medicamentos.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <AnimatePresence data-id="element-60">
        {openModal && <>
            {/* Overlay */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setOpenModal(null)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9990]" data-id="element-61" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-[9995] flex items-center justify-center p-4 pointer-events-none" data-id="element-62">
              <div className="pointer-events-auto bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden relative" data-id="element-63">
                {/* Close button */}
                <button onClick={() => setOpenModal(null)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10" aria-label="Cerrar" data-id="element-64">
                  <XIcon className="w-4 h-4 text-gray-600" data-id="element-65" />
                </button>

                {/* Content */}
                <div className="overflow-y-auto max-h-[85vh] p-6 md:p-8" data-id="element-66">
                  {openModal === 'privacy' ? <PrivacyContent data-id="element-67" /> : <TermsContent data-id="element-68" />}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <div className="prose prose-sm max-w-none" data-id="element-69">
      <h2 className="text-2xl font-serif font-bold text-ancestral-green mb-4" data-id="element-70">
        Política de Privacidad
      </h2>
      <p className="text-xs text-gray-400 mb-6" data-id="element-71">
        Última actualización: {new Date().toLocaleDateString('es-UY')}
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-72">
        1. Información que Recopilamos
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-73">
        Cuando realizas una compra en nuestro sitio, recopilamos información
        personal como tu nombre, dirección de correo electrónico, dirección de
        facturación y datos de pago necesarios para procesar tu pedido.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-74">
        2. Uso de la Información
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-75">
        Utilizamos tu información para:
      </p>
      <ul className="text-sm text-gray-700 space-y-1 mb-4 list-disc pl-5" data-id="element-76">
        <li data-id="element-77">Procesar y completar tu pedido</li>
        <li data-id="element-78">Enviarte el producto digital adquirido</li>
        <li data-id="element-79">Comunicarnos contigo sobre tu compra</li>
        <li data-id="element-80">Mejorar nuestros productos y servicios</li>
        <li data-id="element-81">Cumplir con obligaciones legales</li>
      </ul>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-82">
        3. Protección de Datos
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-83">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger tu información personal contra acceso no autorizado, pérdida o
        alteración. Todos los pagos se procesan a través de plataformas seguras
        y encriptadas.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-84">
        4. Compartir Información
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-85">
        No vendemos, alquilamos ni compartimos tu información personal con
        terceros, excepto cuando sea necesario para procesar tu pedido
        (procesadores de pago, proveedores de servicios de email) o cuando lo
        requiera la ley.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-86">5. Cookies</h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-87">
        Utilizamos cookies para mejorar tu experiencia de navegación, analizar
        el tráfico del sitio y personalizar el contenido. Puedes configurar tu
        navegador para rechazar cookies, aunque esto puede afectar algunas
        funcionalidades del sitio.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-88">
        6. Tus Derechos
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-89">
        Tienes derecho a acceder, corregir o eliminar tu información personal en
        cualquier momento. Para ejercer estos derechos, contáctanos a través de
        nuestro correo electrónico.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-90">
        7. Cambios a esta Política
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-91">
        Nos reservamos el derecho de actualizar esta política de privacidad en
        cualquier momento. Los cambios serán publicados en esta página con la
        fecha de actualización correspondiente.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-92">8. Contacto</h3>
      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-93">
        Si tienes preguntas sobre esta política de privacidad, puedes
        contactarnos a través de nuestro sitio web o correo electrónico de
        soporte.
      </p>
    </div>;
}
function TermsContent() {
  return <div className="prose prose-sm max-w-none" data-id="element-94">
      <h2 className="text-2xl font-serif font-bold text-ancestral-green mb-4" data-id="element-95">
        Términos y Condiciones
      </h2>
      <p className="text-xs text-gray-400 mb-6" data-id="element-96">
        Última actualización: {new Date().toLocaleDateString('es-UY')}
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-97">
        1. Aceptación de los Términos
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-98">
        Al acceder y utilizar este sitio web, aceptas estar sujeto a estos
        términos y condiciones. Si no estás de acuerdo con alguna parte de estos
        términos, no debes utilizar nuestro sitio.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-99">
        2. Producto Digital
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-100">
        "El Gran Recetario Ancestral" es un producto digital (ebook) que se
        entrega inmediatamente después de la compra mediante descarga digital.
        No se envían productos físicos.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-101">
        3. Licencia de Uso
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-102">
        Al comprar este producto, recibes una licencia personal, no transferible
        y no exclusiva para usar el contenido únicamente para tu uso personal.
        Está prohibido:
      </p>
      <ul className="text-sm text-gray-700 space-y-1 mb-4 list-disc pl-5" data-id="element-103">
        <li data-id="element-104">Revender, redistribuir o compartir el producto</li>
        <li data-id="element-105">Modificar, copiar o crear trabajos derivados</li>
        <li data-id="element-106">Usar el contenido con fines comerciales sin autorización</li>
        <li data-id="element-107">Publicar el contenido en sitios web o redes sociales</li>
      </ul>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-108">
        4. Garantía de Devolución
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-109">
        Ofrecemos una garantía de devolución de 60 días. Si no estás satisfecho
        con el producto, puedes solicitar un reembolso completo dentro de los 60
        días posteriores a la compra. Contáctanos con tu número de pedido para
        procesar la devolución.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-110">
        5. Descargo de Responsabilidad Médica
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-111">
        Este producto es únicamente informativo y educativo. No constituye
        asesoramiento médico profesional ni reemplaza la consulta con un médico
        calificado. Los remedios naturales presentados son de carácter
        tradicional y no garantizan resultados específicos.
      </p>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-112">
        <strong data-id="element-113">IMPORTANTE:</strong> Consulta siempre a tu médico antes de usar
        cualquier remedio natural, especialmente si estás embarazada,
        amamantando, tomando medicamentos o tienes condiciones médicas
        preexistentes.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-114">
        6. Limitación de Responsabilidad
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-115">
        No nos hacemos responsables de cualquier daño directo, indirecto,
        incidental o consecuente que pueda resultar del uso o la imposibilidad
        de usar este producto. El uso de los remedios es bajo tu propia
        responsabilidad.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-116">
        7. Propiedad Intelectual
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-117">
        Todo el contenido de este producto, incluyendo textos, imágenes, diseños
        y recetas, está protegido por derechos de autor. Cualquier uso no
        autorizado constituye una violación de las leyes de propiedad
        intelectual.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-118">
        8. Modificaciones
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-119">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Los cambios entrarán en vigor inmediatamente después de su
        publicación en el sitio web.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-120">
        9. Ley Aplicable
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4" data-id="element-121">
        Estos términos se rigen por las leyes de Uruguay. Cualquier disputa será
        resuelta en los tribunales competentes de Uruguay.
      </p>

      <h3 className="text-lg font-bold text-gray-800 mt-6 mb-3" data-id="element-122">
        10. Contacto
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-123">
        Para preguntas sobre estos términos y condiciones, contáctanos a través
        de nuestro sitio web o correo electrónico de soporte.
      </p>
    </div>;
}